// Refills table rows (Collab TSK-9113 / TSK-8926, owner 11-01). A row is one Athena medication merged
// with its saved status (v1/v2 /medications/patient/{pid}/refills).
import moment from "moment";

// The patient app's "delete medication": saved Inactive at once. The server marks new ones
// action=patient_stop and adds patient_stop + stopped_at on every one (older ones included).
export const isPatientStop = (row) => !!row && (row.patient_stop === true || row.action === "patient_stop");

// Room tablet "ask about a new medication" (owner 11-02): listed under Requested (v2 /refills?status=req
// adds new_med_request: true). Not a chart medication and not a refill: no Approve / Reject - staff act on
// the care Task the tablet filed.
export const isNewMedRequest = (row) => !!row && (row.new_med_request === true || row.action === "new_med_request");

// Filed from the room tablet (refill request, "I no longer take this", new medication request).
export const fromRoomTablet = (row) => row?.source === "room_tablet";
const viaTablet = (row) => (fromRoomTablet(row) ? " (room tablet)" : "");

// A refill waiting for staff: refill Y, not approved, not Inactive, not a patient stop.
// The same rule as the server's pending_refills / requested counts and the Requested filter.
export const isPendingRefill = (row) =>
  !!row && row.refill === "Y" && row.refillApprove === "N" && row.status !== "I" && !isPatientStop(row)
  && !isNewMedRequest(row);

// Approve / Reject apply to Athena medications only (never to a new medication request).
export const canDecide = (row) => !!row && !isNewMedRequest(row);

// The Medication cell.
export const rowTitle = (row) =>
  isNewMedRequest(row) ? `New medication requested by patient: ${row.medication || ""}`.trim() : row?.medication;

const day = (value) => (value ? moment(value).format("MM/DD/YYYY") : "");

// { label, tone, sub } for the Status column and the line under the medication name.
// "Requested"/"Pending" only on refill rows (refill Y).
export const rowStatus = (row) => {
  if (isNewMedRequest(row)) {
    const when = day(row.created_at);
    return { label: "New request", tone: "new",
      sub: `${when ? `Requested ${when}` : "Requested"}${viaTablet(row)} · not on the chart; see the Task` };
  }
  if (isPatientStop(row)) {
    const when = day(row.stopped_at || row.created_at);
    return { label: "Inactive", tone: "inactive", sub: `Patient-reported stopped${when ? ` ${when}` : ""}${viaTablet(row)}` };
  }
  if (row?.refill === "Y" && row?.refillApprove === "Y") {
    return { label: "Approved", tone: "ok", sub: row.created_at ? `Requested ${day(row.created_at)}` : "" };
  }
  if (isPendingRefill(row)) {
    return { label: "Pending", tone: "pending",
      sub: row.created_at ? `Requested ${day(row.created_at)}${viaTablet(row)}` : "" };
  }
  if (row?.status === "I") return { label: "Inactive", tone: "inactive", sub: "" };
  return { label: "Active", tone: "active", sub: "" };
};

const joined = (value, unit) => {
  const v = value === null || value === undefined ? "" : String(value).trim();
  if (!v) return "";
  const u = unit === null || unit === undefined ? "" : String(unit).trim();
  return u ? `${v} ${u}` : v;
};

// Quantity / Duration from Athena's own fields (passed through by the gateway when Athena sends them);
// never guessed from the sig text. "" when Athena did not send it.
export const quantityText = (row) => joined(row?.quantity, row?.quantityunit);
export const durationText = (row) =>
  joined(row?.structuredsig?.dosagedurationvalue, row?.structuredsig?.dosagedurationunit);

// The update form for one Approve / Reject (multipart; v1 /medications/patient/{pid}/update).
// Approve: refill Y, status A, userprovider 1. Reject: refill N, status I, userprovider 0
// (was refill "false" from a boolean, and Approve sent userprovider 0 from a typo, so it never approved).
export const refillUpdateFields = (row, decision) =>
  decision === "approve"
    ? { medicationentryid: row.medicationentryid, status: "A", refill: "Y", userprovider: 1 }
    : { medicationentryid: row.medicationentryid, status: "I", refill: "N", userprovider: 0 };
